import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Plus, Globe, ExternalLink, Settings, Pencil, Loader2, Sparkles, Trash2, LayoutTemplate } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { usePerm, usePermScope } from '@/lib/perm';
import { useLang } from '@/lib/i18n';
import { api, errMsg, fmtDate } from '@/lib/api';
import { AppShell, PageHeader, EmptyState } from '@/components/AppShell';
import { ModuleIcon } from '@/components/ModuleIcon';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

export default function SitesPage() {
  const { company } = useAuth();
  const { t, lang } = useLang();
  const navigate = useNavigate();
  const [sites, setSites] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: '', default_lang: 'id' });
  const [busy, setBusy] = useState(false);
  const [toDelete, setToDelete] = useState(null);

  const load = () => company && api.get(`/companies/${company.id}/sites`).then(({ data }) => setSites(data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, [company?.id]); // eslint-disable-line

  const create = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post(`/companies/${company.id}/sites`, { name: form.name, default_lang: form.default_lang });
      toast.success(lang === 'id' ? 'Website dibuat' : 'Website created');
      navigate(`/website/${data.id}/editor`);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    try {
      await api.delete(`/sites/${toDelete.id}`);
      toast.success(lang === 'id' ? 'Website dihapus' : 'Website deleted');
      setToDelete(null);
      load();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  const { isAdmin: canEdit } = usePerm('website');
  usePermScope('website');

  return (
    <AppShell moduleName={t('website')} moduleIcon={<ModuleIcon moduleKey="website" size="sm" />}>
      <div className="mx-auto max-w-6xl px-4 sm:px-6 py-8">
        <PageHeader
          title={t('my_sites')}
          subtitle={lang === 'id' ? 'Bangun dan kelola website bisnis Anda dalam dua bahasa.' : 'Build and manage your bilingual business websites.'}
          actions={canEdit && <Button onClick={() => setOpen(true)} className="rounded-xl" data-testid="website-new-site-button"><Plus size={16} className="mr-1.5" />{t('new_site')}</Button>}
        />
        {sites === null ? (
          <div className="flex justify-center py-20"><Loader2 className="animate-spin text-primary" /></div>
        ) : sites.length === 0 ? (
          <EmptyState icon={LayoutTemplate} title={lang === 'id' ? 'Belum ada website' : 'No websites yet'} description={lang === 'id' ? 'Buat website pertama Anda. Gunakan AI untuk menghasilkan seluruh konten dalam hitungan detik.' : 'Create your first website. Use AI to generate all content in seconds.'} action={canEdit && <Button onClick={() => setOpen(true)} className="rounded-xl" data-testid="website-empty-create-button"><Sparkles size={16} className="mr-1.5" />{t('new_site')}</Button>} />
        ) : (
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {sites.map((s) => (
              <div key={s.id} className="group rounded-2xl border border-border bg-white p-5 shadow-soft transition-shadow hover:shadow-lg fade-up" data-testid={`site-card-${s.slug}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <h3 className="font-display text-lg font-semibold truncate">{s.name}</h3>
                    <a href={s.public_url} target="_blank" rel="noreferrer" className="text-xs text-muted-foreground hover:text-primary truncate flex items-center gap-1">/site/{s.slug} <ExternalLink size={11} /></a>
                  </div>
                  <Badge variant="outline" className={`rounded-full shrink-0 ${s.is_published ? 'bg-emerald-50 text-emerald-800 border-emerald-200' : 'bg-slate-50 text-slate-600'}`} data-testid={`site-status-${s.slug}`}>{s.is_published ? t('published') : t('draft')}</Badge>
                </div>
                <div className="mt-4 h-28 rounded-xl border border-border overflow-hidden relative" style={{ background: s.theme?.primary || '#1B2A6B' }}>
                  <div className="absolute inset-x-4 top-4 h-2 rounded bg-white/70" />
                  <div className="absolute inset-x-4 top-9 h-2 w-2/3 rounded bg-white/40" />
                  <div className="absolute left-4 bottom-4 h-5 w-16 rounded-md" style={{ background: s.theme?.secondary || '#22C5AB' }} />
                </div>
                <div className="mt-4 flex items-center justify-between text-xs text-muted-foreground">
                  <span>{s.pages_count} {t('pages').toLowerCase()} · {s.languages?.join('/').toUpperCase()}</span>
                  <span>{fmtDate(s.updated_at, lang)}</span>
                </div>
                <div className="mt-2 flex items-center gap-1.5 text-xs text-muted-foreground font-mono" data-testid={`site-platform-host-${s.slug}`}><Globe size={12} className={s.is_published ? 'text-emerald-600' : 'text-slate-400'} />{s.platform_host}</div>
                {s.custom_domain && <div className="mt-1 flex items-center gap-1.5 text-xs font-mono"><Globe size={12} className={s.domain_live ? 'text-emerald-600' : s.domain_status === 'verified' ? 'text-sky-600' : 'text-amber-600'} />{s.custom_domain} · <span className="font-sans">{s.domain_live ? 'Live' : s.domain_status === 'verified' ? t('verified') : t('pending')}</span></div>}
                <div className="mt-4 flex gap-2">
                  <Button size="sm" className="rounded-lg flex-1" onClick={() => navigate(`/website/${s.id}/editor`)} data-testid={`site-edit-${s.slug}`}><Pencil size={14} className="mr-1.5" />{t('editor')}</Button>
                  <Button size="sm" variant="outline" className="rounded-lg" onClick={() => navigate(`/website/${s.id}/settings`)} data-testid={`site-settings-${s.slug}`}><Settings size={14} /></Button>
                  {canEdit && <Button size="sm" variant="ghost" className="rounded-lg text-muted-foreground hover:text-destructive" onClick={() => setToDelete(s)} data-testid={`site-delete-${s.slug}`}><Trash2 size={14} /></Button>}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display">{t('new_site')}</DialogTitle>
            <DialogDescription>{lang === 'id' ? 'Beri nama website Anda. Anda bisa mengubahnya nanti.' : 'Name your website. You can change it later.'}</DialogDescription>
          </DialogHeader>
          <form onSubmit={create} className="space-y-4" data-testid="create-site-form">
            <div className="space-y-1.5">
              <Label>{t('site_name')}</Label>
              <Input required minLength={2} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder={company?.name} data-testid="create-site-name-input" className="rounded-xl h-11" />
            </div>
            <div className="space-y-1.5">
              <Label>{lang === 'id' ? 'Bahasa utama' : 'Primary language'}</Label>
              <Select value={form.default_lang} onValueChange={(v) => setForm({ ...form, default_lang: v })}>
                <SelectTrigger className="rounded-xl h-11" data-testid="create-site-lang-select"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="id">Bahasa Indonesia</SelectItem><SelectItem value="en">English</SelectItem></SelectContent>
              </Select>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" className="rounded-xl" onClick={() => setOpen(false)}>{t('cancel')}</Button>
              <Button type="submit" disabled={busy} className="rounded-xl" data-testid="create-site-submit-button">{busy ? <Loader2 className="animate-spin" size={16} /> : t('create')}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>{t('confirm_delete')}</AlertDialogTitle><AlertDialogDescription>{toDelete?.name} — {lang === 'id' ? 'semua halaman akan dihapus permanen.' : 'all pages will be permanently deleted.'}</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel>{t('cancel')}</AlertDialogCancel><AlertDialogAction onClick={remove} className="bg-destructive hover:bg-destructive/90" data-testid="site-delete-confirm">{t('yes_delete')}</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AppShell>
  );
}
